import { IContextualMenuItem } from "@fluentui/react";
import { ModeEnum } from "../../../src/taskpane/components/enums/modeEnum";

type MenuItemOrNull = IContextualMenuItem | null | undefined;

export interface ICommandBarMenuProps {
  items: IContextualMenuItem[];
  farItems: IContextualMenuItem[];
}

export interface ContentManagerMenuItems {
  automation?: MenuItemOrNull;
  checkin?: MenuItemOrNull;
  checkout?: MenuItemOrNull;
  clearFilter?: MenuItemOrNull;
  clearSearch?: MenuItemOrNull;
  copyLink?: MenuItemOrNull;
  copyTo?: MenuItemOrNull;
  currentLocation?: MenuItemOrNull;
  delete?: MenuItemOrNull;
  discardCheckout?: MenuItemOrNull;
  download?: MenuItemOrNull;
  downloadAsPDF?: MenuItemOrNull;
  hixUpload?: MenuItemOrNull;
  error?: MenuItemOrNull;
  filter?: MenuItemOrNull;
  loadingFiles?: MenuItemOrNull;
  more?: MenuItemOrNull;
  moveTo?: MenuItemOrNull;
  moveToCancel?: MenuItemOrNull;
  new?: MenuItemOrNull;
  newDatasourceDocumentTemplate?: MenuItemOrNull;
  open?: MenuItemOrNull;
  pin?: MenuItemOrNull;
  preview?: MenuItemOrNull;
  previewIconOnly?: MenuItemOrNull;
  properties?: MenuItemOrNull;
  propertiesIconOnly?: MenuItemOrNull;
  publish?: MenuItemOrNull;
  quickFilter?: MenuItemOrNull;
  refresh?: MenuItemOrNull;
  rename?: MenuItemOrNull;
  replace?: MenuItemOrNull;
  saveAsPDF?: MenuItemOrNull;
  scanCustomFields?: MenuItemOrNull;
  scanningTemplates?: MenuItemOrNull;
  suggestPlaceholders?: MenuItemOrNull;
  savingFiles?: MenuItemOrNull;
  selectedFileCount?: MenuItemOrNull;
  selectedMoveFileCount?: MenuItemOrNull;
  share?: MenuItemOrNull;
  shareWithGroup?: MenuItemOrNull;
  shareToTeams?: MenuItemOrNull;
  signingRequest?: MenuItemOrNull;
  unpublish?: MenuItemOrNull;
  upload?: MenuItemOrNull;
  versionAppRej?: MenuItemOrNull;
  versionHistory?: MenuItemOrNull;
  views?: MenuItemOrNull;
}

export interface BuildMenuItemsArgs {
  mode: ModeEnum;
  moveToEnabled: boolean;
  /**
   * True when the copy/move tray is on screen. The tray carries confirm and cancel for
   * document operations, so repeating them here would put two identical pairs of buttons
   * a few pixels apart. Email copy modes have no tray and keep theirs. The far-item
   * (count / error / progress) is complementary, not a duplicate, and always stays.
   */
  copyMoveTrayVisible?: boolean;
  menuItems: ContentManagerMenuItems;
}

export interface ContentManagerMenuResult {
  commandBarMenu: ICommandBarMenuProps;
  contextMenu: IContextualMenuItem[];
}

const pushItem = (items: IContextualMenuItem[], item?: MenuItemOrNull) => {
  if (item) {
    items.push(item);
  }
};

export const buildMenuItems = ({ mode, moveToEnabled, copyMoveTrayVisible, menuItems }: BuildMenuItemsArgs): ContentManagerMenuResult => {
  const contextMenu: IContextualMenuItem[] = [];
  const commandBarMenu: ICommandBarMenuProps = {
    items: [],
    farItems: [],
  };

  if (moveToEnabled) {
    if (!copyMoveTrayVisible) {
      pushItem(contextMenu, menuItems.copyTo);
      pushItem(commandBarMenu.items, menuItems.copyTo);

      pushItem(contextMenu, menuItems.moveTo);
      pushItem(commandBarMenu.items, menuItems.moveTo);

      pushItem(contextMenu, menuItems.moveToCancel);
      pushItem(commandBarMenu.items, menuItems.moveToCancel);
    }

    pushItem(commandBarMenu.farItems, menuItems.selectedMoveFileCount);

    return { commandBarMenu, contextMenu };
  }

  if (mode === ModeEnum.contentManagerTeams) {
    pushItem(contextMenu, menuItems.shareToTeams);
  }

  pushItem(commandBarMenu.items, menuItems.new);
  pushItem(commandBarMenu.items, menuItems.upload);
  pushItem(commandBarMenu.items, menuItems.newDatasourceDocumentTemplate);

  pushItem(contextMenu, menuItems.currentLocation);
  pushItem(commandBarMenu.items, menuItems.currentLocation);

  pushItem(contextMenu, menuItems.open);
  pushItem(commandBarMenu.items, menuItems.open);

  pushItem(contextMenu, menuItems.preview);

  pushItem(contextMenu, menuItems.share);
  pushItem(commandBarMenu.items, menuItems.share);

  pushItem(contextMenu, menuItems.shareWithGroup);
  pushItem(commandBarMenu.items, menuItems.shareWithGroup);

  pushItem(contextMenu, menuItems.copyLink);
  pushItem(commandBarMenu.items, menuItems.copyLink);

  pushItem(contextMenu, menuItems.rename);
  pushItem(commandBarMenu.items, menuItems.rename);

  pushItem(contextMenu, menuItems.replace);
  pushItem(commandBarMenu.items, menuItems.replace);

  pushItem(contextMenu, menuItems.download);
  pushItem(commandBarMenu.items, menuItems.download);

  pushItem(contextMenu, menuItems.downloadAsPDF);
  pushItem(commandBarMenu.items, menuItems.downloadAsPDF);

  pushItem(contextMenu, menuItems.saveAsPDF);
  pushItem(commandBarMenu.items, menuItems.saveAsPDF);

  pushItem(contextMenu, menuItems.hixUpload);
  pushItem(commandBarMenu.items, menuItems.hixUpload);

  pushItem(contextMenu, menuItems.scanCustomFields);
  pushItem(commandBarMenu.items, menuItems.scanCustomFields);

  // AI placeholder suggestions live directly beside the scan command: same location and
  // permission gating, plus the per-tenant TemplateAiEnabled opt-in inside the hook.
  pushItem(contextMenu, menuItems.suggestPlaceholders);
  pushItem(commandBarMenu.items, menuItems.suggestPlaceholders);

  pushItem(contextMenu, menuItems.automation);
  pushItem(commandBarMenu.items, menuItems.automation);

  pushItem(contextMenu, menuItems.signingRequest);
  pushItem(commandBarMenu.items, menuItems.signingRequest);

  pushItem(contextMenu, menuItems.copyTo);
  pushItem(commandBarMenu.items, menuItems.copyTo);

  pushItem(contextMenu, menuItems.moveTo);
  pushItem(commandBarMenu.items, menuItems.moveTo);

  pushItem(contextMenu, menuItems.delete);
  pushItem(commandBarMenu.items, menuItems.delete);

  pushItem(contextMenu, menuItems.pin);
  pushItem(commandBarMenu.items, menuItems.pin);

  pushItem(contextMenu, menuItems.versionHistory);
  pushItem(contextMenu, menuItems.more);
  pushItem(contextMenu, menuItems.properties);

  pushItem(commandBarMenu.items, menuItems.versionHistory);
  pushItem(commandBarMenu.items, menuItems.checkout);
  pushItem(commandBarMenu.items, menuItems.checkin);
  pushItem(commandBarMenu.items, menuItems.discardCheckout);
  pushItem(commandBarMenu.items, menuItems.publish);
  pushItem(commandBarMenu.items, menuItems.unpublish);
  pushItem(commandBarMenu.items, menuItems.versionAppRej);
  pushItem(commandBarMenu.items, menuItems.quickFilter);

  pushItem(commandBarMenu.farItems, menuItems.selectedFileCount);
  pushItem(commandBarMenu.farItems, menuItems.clearSearch);
  pushItem(commandBarMenu.farItems, menuItems.clearFilter);
  pushItem(commandBarMenu.farItems, menuItems.error);
  pushItem(commandBarMenu.farItems, menuItems.savingFiles);
  pushItem(commandBarMenu.farItems, menuItems.scanningTemplates);
  pushItem(commandBarMenu.farItems, menuItems.selectedMoveFileCount);
  pushItem(commandBarMenu.farItems, menuItems.loadingFiles);
  pushItem(commandBarMenu.farItems, menuItems.propertiesIconOnly);
  pushItem(commandBarMenu.farItems, menuItems.previewIconOnly);
  pushItem(commandBarMenu.farItems, menuItems.views);
  pushItem(commandBarMenu.farItems, menuItems.filter);
  pushItem(commandBarMenu.farItems, menuItems.refresh);

  return { commandBarMenu, contextMenu };
};
